import { useEffect, useRef, useState } from "react";
import PSPDFKit from 'pspdfkit';
import { useGetDocumentById } from "../../../hooks/ReactQueryHooks/useGetDocument";
import { PSPDFKIT_LICENSE_KEY } from "../../../environment";

const SIGNATURE_FIELD_NAME = "practitioner-signature_af_image";

const stampSignature = async (instance, signUrl) => {
    const blob = await fetch(signUrl).then((res) => res.blob());
    const totalPages = instance.totalPageCount;

    for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        const annotations = await instance.getAnnotations(pageIndex);
        const signatureFields = annotations.filter(
            (annotation) =>
                annotation instanceof PSPDFKit.Annotations.WidgetAnnotation &&
                (annotation.formFieldName === SIGNATURE_FIELD_NAME ||
                    annotation.formFieldName?.includes("practitioner-signature"))
        );

        for (const signatureField of signatureFields) {
            const imageAttachmentId = await instance.createAttachment(blob);
            const imageAnnotation = new PSPDFKit.Annotations.ImageAnnotation({
                pageIndex,
                boundingBox: signatureField.boundingBox,
                contentType: "image/jpeg",
                imageAttachmentId,
            });
            await instance.create(imageAnnotation);
        }
    }
};

export const populatePdfForm = async (instance, initialPdfFormValues) => {
    if (!instance || !initialPdfFormValues) return;

    const formFields = await instance.getFormFields();

    for (const formField of formFields) {
        const value = initialPdfFormValues[formField.name];
        if (!value) continue;

        if (formField.name === SIGNATURE_FIELD_NAME) {
            await stampSignature(instance, value);
        } else {
            instance.setFormFieldValues({ [formField.name]: value });
        }
    }
};

export const validatePdfBeforeNext = async (instance, initialPdfFormValues) => {
    if (!initialPdfFormValues) {
        return { ok: false, message: "Prescription data is still loading. Please wait and try again." };
    }
    if (!instance) {
        return { ok: false, message: "PDF is still loading. Please wait and try again." };
    }

    const formFields = await instance.getFormFields();
    const signatureRequired = formFields.some((f) => f.name === SIGNATURE_FIELD_NAME);
    const signatureOnProfile = Boolean(initialPdfFormValues[SIGNATURE_FIELD_NAME]);

    if (signatureRequired && !signatureOnProfile) {
        return {
            ok: false,
            message: "No signature on your profile. Please update your profile before continuing.",
        };
    }

    if (signatureRequired && signatureOnProfile) {
        try {
            await populatePdfForm(instance, initialPdfFormValues);
        } catch (err) {
            return {
                ok: false,
                message: err?.message || "Could not apply signature to the prescription. Please try again.",
            };
        }
    }

    return { ok: true };
};

export default function PdfViewerComponent({ document, documentId, instanceRef, initialPdfFormValues }) {
    const containerRef = useRef(null);
    const [base64Url, setBase64Url] = useState(null);

    const onSuccess = (data) => {
        setBase64Url(data?.content?.[0]?.attachment?.base64String);
    };
    useGetDocumentById({ onSuccess, id: documentId });

    useEffect(() => {
        const baseUrl = `${window.location.protocol}//${window.location.host}/`;
        const container = containerRef.current;
        let cancelled = false;
        const pdfSource = document || base64Url;

        if (!pdfSource) {
            return undefined;
        }

        (async () => {
            try {
                const instance = await PSPDFKit.load({
                    disableWebAssemblyStreaming: true,
                    container,
                    document: pdfSource,
                    toolbarItems: [],
                    baseUrl,
                    licenseKey: PSPDFKIT_LICENSE_KEY,
                });

                if (cancelled) {
                    PSPDFKit.unload(container);
                    return;
                }

                instance.setViewState((viewState) => viewState.set("showToolbar", false));
                instanceRef.current = instance;

                if (initialPdfFormValues) {
                    await populatePdfForm(instance, initialPdfFormValues);
                }
            } catch (err) {
                console.error("PDF viewer failed to load:", err);
            }
        })();

        return () => {
            cancelled = true;
            if (container && PSPDFKit) {
                PSPDFKit.unload(container);
            }
        };
    }, [document, base64Url, initialPdfFormValues, instanceRef]);

    return <div ref={containerRef} style={{ width: "100%", height: "80vh" }} />;
};
